import React, { useState, useRef } from 'react';
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useManagementStore } from '../../../store/managementStore';

// 신규 일정 등록 모달 컴포넌트
const RegisterScheduleModal = ({ isOpen, onClose, eventData, onSuccess }) => {
  // 상태 관리
  const [scheduleName, setScheduleName] = useState('');
  const [guideName, setGuideName] = useState('');
  const [guidePhone, setGuidePhone] = useState('');
  const [meetingPlace, setMeetingPlace] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [s3PublicUrl, setS3PublicUrl] = useState(null);
  const fileInputRef = useRef(null);
  
  // managementStore에서 API 함수 가져오기
  const uploadScheduleImage = useManagementStore((state) => state.uploadScheduleImage);
  const registerSchedule = useManagementStore((state) => state.registerSchedule);
  
  // 입력 값 검증
  const validateInput = () => {
    if (!scheduleName.trim()) {
      alert('일정명을 입력해주세요.');
      return false;
    }
    if (!selectedFile) {
      alert('일정 이미지를 업로드해주세요.');
      return false;
    }
    return true;
  };
  
  // 일정 이미지 업로드 함수
  const handleUploadImage = async () => {
    if (!validateInput()) return false;
    
    setIsUploading(true);
    try {
      // eventData가 없는 경우 빈 객체 사용
      const eventDataToUse = eventData || {};
      const result = await uploadScheduleImage(selectedFile, eventDataToUse);
      if (result.success) {
        setS3PublicUrl(result.data.public_url);
        return true;
      } else {
        alert('이미지 업로드 중 오류가 발생했습니다: ' + result.error);
        return false;
      }
    } catch (error) {
      console.error('이미지 업로드 오류:', error);
      alert('이미지 업로드 중 오류가 발생했습니다.');
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  // 상세 입력값 검증 함수
  function validateDetailedInput(publicUrl) {
    // 사용할 URL - 파라미터로 전달된 URL 또는 상태의 URL 사용
    const urlToValidate = publicUrl || s3PublicUrl;
    
    // 빈 값 또는 유효하지 않은 값 체크
    if (urlToValidate === undefined || urlToValidate === null || urlToValidate === "" ||
        urlToValidate === "undefined" || urlToValidate === "null" ||
        urlToValidate.includes("undefined") || urlToValidate.includes("null") ||
        urlToValidate === " ") {
      alert("파일 등록 후 다시 시도해주세요.");
      return false;
    }
    
    // HTML 및 URL에서 문제가 될 수 있는 특수 문자 정규식
    // 파일명에는 점(.)과 슬래시(/)는 허용 (URL 경로와 확장자에 필요)
    const problematicCharsRegex = /['"\\\[\]<>{}|^~`!@#$%&*+=:;?]/;
    
    const getFilenameFromUrl = (url) => {
      const parts = url.split('/');
      return parts[parts.length - 1];
    };
    
    // 검사할 필드와 해당 필드의 표시 이름
    const fieldsToValidate = [
      { value: getFilenameFromUrl(decodeURIComponent(s3PublicUrl)), displayName: '일정 이미지 이름' },
      { value: scheduleName, displayName: '일정 이름' },
      { value: guideName, displayName: '가이드 이름' },
      { value: guidePhone, displayName: '가이드 전화번호' },
      { value: meetingPlace, displayName: '미팅장소' }
    ];
    
    // 각 필드에 대해 특수 문자 검사
    for (const field of fieldsToValidate) {
      if (!field.value || field.value.trim() === "") {
        alert(`${field.displayName}을(를) 입력해주세요.`);
        return false;
      }
      
      if (field.value && problematicCharsRegex.test(field.value)) {
        alert(`${field.displayName}에 특수문자가 포함되어 있습니다. 다시 시도해주세요.`);
        return false;
      }
    }
    
    return true;
  }
  
  // 일정 등록 함수
  const handleRegisterSchedule = async () => {
    // 기본 입력값 검증
    if (!validateInput()) return;
    
    // 로딩 상태 설정
    setIsUploading(true);
    
    // 이미지 업로드 및 등록 진행
    try {
      // eventData가 없는 경우 빈 객체 사용
      const eventDataToUse = eventData || {};
      
      // 이미지 업로드
      const imageResult = await uploadScheduleImage(selectedFile, eventDataToUse);
      
      if (!imageResult.success) {
        alert('이미지 업로드 중 오류가 발생했습니다: ' + imageResult.error);
        return;
      }
      
      // 업로드된 URL 가져오기
      const uploadedUrl = imageResult.data.public_url;
      
      // 상세 입력값 검증 (업로드된 URL 전달)
      if (!validateDetailedInput(uploadedUrl)) return;
      
      // s3PublicUrl 상태 업데이트
      setS3PublicUrl(uploadedUrl);
      
      // 일정 등록 데이터 준비
      const scheduleData = {
        GOOD_TYPE_CD: eventDataToUse.GOOD_TYPE_CD.toString() || '',
        AREA_CD: eventDataToUse.AREA_CD.toString() || '',
        GOOD_YY: eventDataToUse.GOOD_YY.toString() || '',
        GOOD_SEQ: eventDataToUse.GOOD_SEQ.toString() || '',
        nameString: scheduleName,
        s3PublicUrl: uploadedUrl, // 업로드된 URL 사용
        guideNameString: guideName,
        guidePhoneString: guidePhone,
        meetingPlaceString: meetingPlace
      };
      
      // 일정 등록 API 호출
      const registerResult = await registerSchedule(scheduleData);
      if (registerResult.success) {
        alert('일정 등록이 성공적으로 완료되었습니다.');
        resetForm();
        onClose();
        
        // 일정 등록 성공 후 데이터 새로 불러오기
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess();
        }
      } else {
        alert('일정 등록 중 오류가 발생했습니다: ' + registerResult.error);
      }
    } catch (error) {
      console.error('일정 등록 오류:', error);
      alert('일정 등록 중 오류가 발생했습니다.');
    } finally {
      // 로딩 상태 해제
      setIsUploading(false);
    }
  };

  // 파일 선택 핸들러
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setSelectedFile(file);
    
    // 파일 미리보기 URL 생성
    const fileUrl = URL.createObjectURL(file);
    setPreviewUrl(fileUrl);
  };

  // 파일 선택 버튼 클릭 핸들러
  const handleSelectFileClick = () => {
    fileInputRef.current.click();
  };

  // 폼 초기화
  const resetForm = () => {
    setScheduleName('');
    setGuideName('');
    setGuidePhone('');
    setMeetingPlace('');
    setSelectedFile(null);
    setPreviewUrl(null);
    setS3PublicUrl(null);
  };

  // 모달 닫기 핸들러
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // 모달이 닫혀 있으면 렌더링하지 않음
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* 배경 오버레이 */}
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={handleClose}></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        {/* 모달 내용 */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* 모달 헤더 */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-center pb-3 border-b mb-4">
              <h3 className="text-lg font-semibold text-gray-900">신규 일정 등록</h3>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* 안내 메시지 */}
            <div
              className="mb-4 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded relative"
              role="alert"
            >
              <div className="flex">
                <div className="py-1">
                  <svg
                    className="fill-current h-6 w-6 text-blue-500 mr-4"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                  >
                    <path d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z" />
                  </svg>
                </div>
                <div>
                  <p className="font-bold">
                    여행 일정을 등록합니다.
                  </p>
                  <p className="text-sm">
                    여행 일정 정보를 입력하고 이미지를 업로드하세요.
                  </p>
                </div>
              </div>
            </div>

            {/* 폼 내용 */}
            <div className="space-y-4">
              {/* 일정명 */}
              <div>
                <label htmlFor="scheduleName" className="block text-sm font-medium text-gray-700">
                  일정명
                </label>
                <input
                  type="text"
                  id="scheduleName"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="일정명을 입력하세요"
                  value={scheduleName}
                  onChange={(e) => setScheduleName(e.target.value)}
                />
              </div>
              
              {/* 가이드 이름 */}
              <div>
                <label htmlFor="guideName" className="block text-sm font-medium text-gray-700">
                  가이드 이름
                </label>
                <input
                  type="text"
                  id="guideName"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="가이드 이름을 입력하세요"
                  value={guideName}
                  onChange={(e) => setGuideName(e.target.value)}
                />
              </div>
              
              {/* 가이드 연락처 */}
              <div>
                <label htmlFor="guidePhone" className="block text-sm font-medium text-gray-700">
                  가이드 연락처
                </label>
                <input
                  type="text"
                  id="guidePhone"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="가이드 연락처를 입력하세요"
                  value={guidePhone}
                  onChange={(e) => setGuidePhone(e.target.value)}
                />
              </div>
              
              {/* 미팅장소 */}
              <div>
                <label htmlFor="meetingPlace" className="block text-sm font-medium text-gray-700">
                  미팅장소
                </label>
                <input
                  type="text"
                  id="meetingPlace"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="미팅장소를 입력하세요"
                  value={meetingPlace}
                  onChange={(e) => setMeetingPlace(e.target.value)}
                />
              </div>
              
              {/* 파일 업로드 */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  파일 선택
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    {previewUrl ? (
                      <div className="flex flex-col items-center">
                        <img
                          src={previewUrl}
                          alt="미리보기"
                          className="max-h-40 max-w-full mb-3"
                        />
                        <p className="text-sm text-gray-500">{selectedFile?.name}</p>
                      </div>
                    ) : (
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                      >
                        <span>파일 선택하기</span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          className="sr-only"
                          ref={fileInputRef}
                          onChange={handleFileChange}
                          accept="image/*"
                        />
                      </label>
                    </div>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, GIF 파일만 업로드 가능합니다.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 모달 푸터 */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="w-full inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={handleRegisterSchedule}
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  처리중...
                </>
              ) : '일정등록'}
            </button>
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={handleClose}
              disabled={isUploading}
            >
              취소
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterScheduleModal;